/**
 * Product Customization Component
 * 
 * Dynamic customization modal that loads options from the database.
 * Works for any customizable product (sandwiches, trays, etc.)
 * 
 * Features:
 * - Loads option groups dynamically based on category or item
 * - Supports single-select (radio) and multi-select (checkbox)
 * - Shows price adjustments for premium options
 * - Calculates total price including add-ons
 */

import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { publicLoadErrorMessage } from '../lib/publicSiteErrorMessage';

// Types matching database schema
interface ProductOption {
  id: string;
  name: string;
  emoji: string | null;
  image_url: string | null;
  price_adjustment: number;
  is_default: boolean;
  is_available: boolean;
  display_order: number;
}

interface OptionGroup {
  id: string;
  name: string;
  description: string | null;
  selection_type: 'single' | 'multi';
  is_required: boolean;
  min_selections: number;
  max_selections: number | null;
  category_name: string | null;
  display_order: number;
  options: ProductOption[];
}

// Selected options format for cart
export interface ProductCustomizations {
  [groupId: string]: {
    groupName: string;
    selections: {
      id: string;
      name: string;
      price: number;
    }[];
  };
}

interface ProductCustomizationProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (customizations: ProductCustomizations, extraCost: number) => void;
  itemName: string;
  itemPrice: number;
  category: string;
  menuItemId?: string;
}

export default function ProductCustomization({
  isOpen,
  onClose,
  onConfirm,
  itemName,
  itemPrice,
  category,
}: ProductCustomizationProps) {
  const [optionGroups, setOptionGroups] = useState<OptionGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Track selected options per group
  const [selections, setSelections] = useState<Record<string, string[]>>({});

  // Fetch option groups for this category
  useEffect(() => {
    if (!isOpen) return;

    const fetchOptions = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const params = new URLSearchParams();
        if (category) params.append('category', category);
        
        const response = await fetch(`/api/product-options?${params}`);
        if (!response.ok) {
          console.error('Product options fetch failed:', response.status);
          setError(publicLoadErrorMessage());
          return;
        }

        const data = await response.json();
        const groups = data.groups || [];
        setOptionGroups(groups);
        
        // Initialize selections with defaults
        const initialSelections: Record<string, string[]> = {};
        groups.forEach((group: OptionGroup) => {
          const defaults = group.options
            .filter(opt => opt.is_default)
            .map(opt => opt.id);
          initialSelections[group.id] = defaults;
        });
        setSelections(initialSelections);
        
      } catch (err) {
        console.error('Product options load:', err);
        setError(publicLoadErrorMessage());
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, [isOpen, category]);

  // Handle selection changes
  const handleSelect = (groupId: string, optionId: string, selectionType: 'single' | 'multi') => {
    setSelections(prev => {
      if (selectionType === 'single') {
        // Single select: replace selection
        return { ...prev, [groupId]: [optionId] };
      } else {
        // Multi select: toggle
        const current = prev[groupId] || [];
        if (current.includes(optionId)) {
          return { ...prev, [groupId]: current.filter(id => id !== optionId) };
        } else {
          return { ...prev, [groupId]: [...current, optionId] };
        }
      }
    });
  };

  // Calculate extra cost from selections
  const calculateExtraCost = (): number => {
    let extra = 0;
    optionGroups.forEach(group => {
      const selected = selections[group.id] || [];
      group.options.forEach(option => {
        if (selected.includes(option.id) && option.price_adjustment > 0) {
          extra += option.price_adjustment;
        }
      });
    });
    return extra;
  };

  // Build customizations object for cart
  const buildCustomizations = (): ProductCustomizations => {
    const result: ProductCustomizations = {};
    
    optionGroups.forEach(group => {
      const selected = selections[group.id] || [];
      if (selected.length > 0) {
        result[group.id] = {
          groupName: group.name,
          selections: group.options
            .filter(opt => selected.includes(opt.id))
            .map(opt => ({
              id: opt.id,
              name: opt.name,
              price: opt.price_adjustment,
            })),
        };
      }
    });
    
    return result;
  };

  // Validate required selections
  const isValid = (): boolean => {
    return optionGroups.every(group => {
      if (!group.is_required) return true;
      const selected = selections[group.id] || [];
      return selected.length >= (group.min_selections || 1);
    });
  };

  // Handle confirm
  const handleConfirm = () => {
    const customizations = buildCustomizations();
    const extraCost = calculateExtraCost();
    onConfirm(customizations, extraCost);
    onClose();
  };

  if (!isOpen) return null;

  const extraCost = calculateExtraCost();
  const totalPrice = itemPrice + extraCost;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-display font-bold text-gray-900">
              Customize Your Order
            </h2>
            <p className="text-gray-600 mt-1">{itemName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {loading && (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading options...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && optionGroups.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">No customization options available.</p>
            </div>
          )}

          {!loading && !error && optionGroups.map(group => (
            <div key={group.id}>
              <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                {group.name}
                {group.is_required && (
                  <span className="text-xs text-red-600 font-normal">(required)</span>
                )}
              </h3>
              {group.description && (
                <p className="text-sm text-gray-600 mb-4">{group.description}</p>
              )}
              
              <div className={`grid gap-3 ${
                group.options.length <= 4 
                  ? 'grid-cols-2' 
                  : 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5'
              }`}>
                {group.options.map(option => {
                  const isSelected = (selections[group.id] || []).includes(option.id);
                  
                  return (
                    <button
                      key={option.id}
                      onClick={() => handleSelect(group.id, option.id, group.selection_type)}
                      className={`p-3 border-2 rounded-lg transition-all text-center ${
                        isSelected
                          ? 'border-primary-600 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {/* Emoji or image */}
                      {option.image_url ? (
                        <img
                          src={option.image_url}
                          alt={option.name}
                          className="w-12 h-12 mx-auto mb-2 object-cover rounded"
                          onError={(e) => {
                            // Fallback to emoji
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="text-3xl mb-2">{option.emoji || '⚪'}</div>
                      )}
                      
                      {/* Name */}
                      <div className={`text-xs font-medium ${
                        isSelected ? 'text-primary-700' : 'text-gray-700'
                      }`}>
                        {option.name}
                      </div>
                      
                      {/* Price adjustment */}
                      {option.price_adjustment > 0 && (
                        <div className="text-xs text-green-600 mt-1">
                          +${option.price_adjustment.toFixed(2)}
                        </div>
                      )}
                      
                      {/* Selection indicator */}
                      {isSelected && (
                        <div className="mt-1 text-primary-600 text-xs">✓</div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-600">Base price: ${itemPrice.toFixed(2)}</p>
              {extraCost > 0 && (
                <p className="text-sm text-green-600">+ Add-ons: ${extraCost.toFixed(2)}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-primary-700">${totalPrice.toFixed(2)}</p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!isValid()}
              className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

